import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, Table, Alert, Spinner, Button, Modal } from "react-bootstrap";
import { FaEdit, FaTrash, FaArrowLeft } from "react-icons/fa";
import ConfirmModal from "../components/modals/ConfirmModal";
import AddEditSiteManagers from "../components/modals/AddEditSiteManagers";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function SiteManagers() {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { siteId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [managers, setManagers] = useState([]);
  const [showAddEditManagerModal, setShowAddEditManagerModal] = useState(false);
  const [editingManagerData, setEditingManagerData] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [managerToDelete, setManagerToDelete] = useState(null);

  // Determine which type based on current route
  const getManagerType = () => {
    if (location.pathname.includes("branch-managers")) return "branch";
    if (location.pathname.includes("plant-managers")) return "plant";
  };

  const type = getManagerType();

  // Get display names based on type
  const getDisplayName = () => {
    if (type === "branch") return "Branch";
    if (type === "plant") return "Plant";
  };

  const displayName = getDisplayName();

  // Format address as comma-separated string from multiple fields
  const formatAddress = (manager) => {
    const addressParts = [
      manager.address_line_1,
      manager.address_line_2,
      manager.city,
      manager.state,
      manager.country,
    ].filter((part) => part && part.trim());

    return addressParts.length > 0 ? addressParts.join(", ") : "N/A";
  };

  // Fetch all managers based on type (all branches/plants)
  const fetchManagers = useCallback(async () => {
    if (!userData?.company_code) {
      setError("Company code not available");
      return;
    }
    if (!siteId) {
      setError("Site ID not available");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/sites/managers/get-all-managers-by-type`,
        {
          params: {
            company_code: userData.company_code,
            type: type,
            site_id: siteId,
          },
        }
      );
      
      if (response.data.success) {
        setManagers(response.data.managers || []);
      } else {
        setError(response.data.message || `Failed to fetch ${type} managers`);
      }
    } catch (err) {
      console.error(`Error fetching ${type} managers:`, err);
      setError(
        err.response?.data?.message || `Error fetching ${type} managers`
      );
    } finally {
      setLoading(false);
    }
  }, [type, userData?.company_code, siteId]);

  // Delete manager
  const confirmDeleteManager = async () => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/api/admin/sites/managers/delete-manager/${managerToDelete}`,
        {
          params: {
            company_code: userData.company_code,
            type: type,
          },
        }
      );

      if (response.data.success) {
        fetchManagers();
      } else {
        setError(response.data.message || `Failed to delete ${type} manager`);
      }
    } catch (err) {
      console.error(`Error deleting ${type} manager:`, err);
      setError(err.response?.data?.message || `Error deleting ${type} manager`);
    }
    setShowConfirmDelete(false);
    setManagerToDelete(null);
  };

  const handleEdit = (manager) => {
    setEditingManagerData(manager);
    setShowAddEditManagerModal(true);
  };

  const handleCloseModal = () => {
    setShowAddEditManagerModal(false);
    setEditingManagerData(null);
  };

  const handleSuccess = () => {
    setShowAddEditManagerModal(false);
    setEditingManagerData(null);
    fetchManagers();
  };

  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  return (
    <div className="admin-container mt-4">
      <div className="style-header flex justify-content">
        <h1>{displayName} Managers Management</h1>
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          style={{ padding: "10px" }}
        >
          <FaArrowLeft /> Go Back
        </Button>
      </div>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="loc-titles">{displayName} Managers List</h3>
            <Button onClick={() => setShowAddEditManagerModal(true)}>
              Add {displayName} Manager
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : managers.length === 0 ? (
            <Alert variant="info">No {type} managers found</Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>{displayName} Name</th>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Email</th>
                  {/* <th>Account Details</th> */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {managers.map((manager) => (
                  <tr key={manager.id || manager.emp_id}>
                    <td>{manager.site_name || manager.site_name || "N/A"}</td>
                    <td>{manager.emp_id || "N/A"}</td>
                    <td>
                      {manager.first_name && manager.last_name
                        ? `${manager.first_name} ${manager.last_name}`
                        : manager.name || "N/A"}
                    </td>
                    <td>{formatAddress(manager)}</td>
                    <td>
                      {manager.country_code && manager.phone
                        ? `${manager.country_code}${manager.phone}`
                        : manager.phone || "N/A"}
                    </td>
                    <td>{manager.email || "N/A"}</td>
                    {/* <td>{manager.bank_account || "N/A"}</td> */}
                    <td>
                      <div className="d-flex gap-2">
                        <FaEdit
                          className="action-icon me-2"
                          onClick={() => handleEdit(manager)}
                          title="Edit"
                          style={{ cursor: "pointer", color: "#007bff" }}
                        />
                        <FaTrash
                          className="action-icon text-danger"
                          onClick={() => {
                            setManagerToDelete(manager.id);
                            setShowConfirmDelete(true);
                          }}
                          title="Delete"
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add Edit Manager Modal */}
      {showAddEditManagerModal && (
        <Modal
          show={showAddEditManagerModal}
          onHide={handleCloseModal}
          dialogClassName="vcm-modal-dialog"
          centered
        >
          <Modal.Header className="vcm-modal-header" closeButton>
            <Modal.Title>
              {editingManagerData
                ? `Update ${displayName} Manager`
                : `Add ${displayName} Manager`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddEditSiteManagers
              editData={editingManagerData}
              onSuccess={handleSuccess}
              type={type}
              currentSiteId={siteId}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showConfirmDelete}
        onHide={() => {
          setShowConfirmDelete(false);
          setManagerToDelete(null);
        }}
        onConfirm={confirmDeleteManager}
        title={`Delete ${displayName} Manager`}
        message={`Are you sure you want to delete this ${type} manager? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={loading}
      />
    </div>
  );
}
